import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useAuth } from "../auth/AuthProvider";
import { useOrgContext } from "../context/OrgContext";

/**
 * Global Cmd-K command palette. Activates with Cmd+K / Ctrl+K anywhere in
 * the cloud UI. Fuzzy-searches against:
 *
 *   • Top-level navigation actions for the active org / venue
 *   • The current user's organisations
 *   • Venues belonging to the active org
 *   • FPZERO-only sections (when the user is an FPZERO admin)
 *
 * Each result is a destination URL — selecting it triggers a `navigate(...)`.
 *
 * Future expansion (driver / booking fuzzy search) needs the relevant cloud
 * endpoints; for v1 we keep the surface area lean and avoid pre-loading
 * entire driver lists.
 */
interface PaletteEntry {
  id: string;
  label: string;
  hint?: string;
  group: string;
  to: string;
  keywords?: string[];
}

export function CommandPalette() {
  const { user } = useAuth();
  const { orgs, currentOrg, venues, currentVenue } = useOrgContext();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
        setQ("");
        setActiveIdx(0);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const entries = useMemo<PaletteEntry[]>(() => {
    const items: PaletteEntry[] = [];

    // FPZERO sections
    if (user?.isFpzeroAdmin) {
      items.push(
        { id: "ops", label: "Operations Overview", group: "FPZERO", to: "/" },
        { id: "orgs", label: "Organisations", group: "FPZERO", to: "/admin/organisations" },
        { id: "releases", label: "Releases", group: "FPZERO", to: "/admin/releases" },
        { id: "licences", label: "Licences", group: "FPZERO", to: "/admin/licences" },
        { id: "audit-fpz", label: "Cross-org audit log", group: "FPZERO", to: "/admin/audit" },
      );
    }

    // Org switcher
    for (const o of orgs) {
      items.push({
        id: `org-${o.id}`,
        label: o.name,
        hint: "Switch organisation",
        group: "Organisations",
        to: `/org/${o.slug}`,
        keywords: [o.slug],
      });
    }

    // Org-scoped jumps for the active org
    if (currentOrg) {
      const base = `/org/${currentOrg.slug}`;
      items.push(
        { id: "org-dash", label: "Dashboard", group: currentOrg.name, to: base },
        { id: "org-venues", label: "Venues", group: currentOrg.name, to: `${base}/venues` },
        { id: "org-drivers", label: "Drivers", group: currentOrg.name, to: `${base}/drivers` },
        { id: "org-bookings", label: "Bookings", group: currentOrg.name, to: `${base}/bookings` },
        {
          id: "org-templates-branding",
          label: "Branding templates",
          group: currentOrg.name,
          to: `${base}/templates/branding`,
        },
        {
          id: "org-templates-race",
          label: "Race templates",
          group: currentOrg.name,
          to: `${base}/templates/race`,
        },
        {
          id: "org-templates-difficulty",
          label: "Difficulty templates",
          group: currentOrg.name,
          to: `${base}/templates/difficulty`,
        },
        { id: "org-partners", label: "Partner keys", group: currentOrg.name, to: `${base}/partners` },
        { id: "org-members", label: "Members", group: currentOrg.name, to: `${base}/members` },
        { id: "org-settings", label: "Settings", group: currentOrg.name, to: `${base}/settings` },
      );

      // Venue jumps for the active org
      for (const v of venues) {
        items.push({
          id: `venue-${v.id}`,
          label: v.name,
          hint: "Open venue dashboard",
          group: "Venues",
          to: `${base}/venue/${v.slug}`,
          keywords: [v.slug],
        });
      }

      // Venue-scoped jumps for the active venue
      if (currentVenue) {
        const vbase = `${base}/venue/${currentVenue.slug}`;
        items.push(
          { id: "v-dash", label: "Venue dashboard", group: currentVenue.name, to: vbase },
          { id: "v-events", label: "Events", group: currentVenue.name, to: `${vbase}/events` },
          { id: "v-sessions", label: "Sessions", group: currentVenue.name, to: `${vbase}/sessions` },
          {
            id: "v-laps",
            label: "Lap times",
            group: currentVenue.name,
            to: `${vbase}/lap-times`,
          },
          {
            id: "v-leaderboards",
            label: "Leaderboards",
            group: currentVenue.name,
            to: `${vbase}/leaderboards`,
          },
          {
            id: "v-bookings",
            label: "Bookings (this venue)",
            group: currentVenue.name,
            to: `${vbase}/bookings`,
          },
          { id: "v-audit", label: "Audit", group: currentVenue.name, to: `${vbase}/audit` },
          { id: "v-settings", label: "Venue settings", group: currentVenue.name, to: `${vbase}/settings` },
        );
      }
    }

    return items;
  }, [user, orgs, currentOrg, venues, currentVenue]);

  const filtered = useMemo(() => {
    if (!q.trim()) return entries.slice(0, 80);
    const needle = q.toLowerCase();
    return entries
      .map((e) => ({
        e,
        score: scoreMatch(e, needle),
      }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 80)
      .map((s) => s.e);
  }, [entries, q]);

  useEffect(() => {
    setActiveIdx(0);
  }, [q]);

  if (!open || !user) return null;

  function jump(entry: PaletteEntry) {
    setOpen(false);
    setQ("");
    navigate(entry.to);
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((idx) => Math.min(filtered.length - 1, idx + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((idx) => Math.max(0, idx - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const choice = filtered[activeIdx];
      if (choice) jump(choice);
    }
  }

  // Group consecutive entries by group label.
  const grouped: Array<{ group: string; entries: PaletteEntry[] }> = [];
  for (const e of filtered) {
    const last = grouped[grouped.length - 1];
    if (last && last.group === e.group) last.entries.push(e);
    else grouped.push({ group: e.group, entries: [e] });
  }

  let runningIdx = 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4"
      onClick={() => setOpen(false)}
    >
      <div className="fixed inset-0 bg-black/40" />
      <div
        className="relative z-10 w-full max-w-xl rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Jump to org, venue, page…"
          className="w-full px-4 py-3 text-sm border-b border-gray-200 focus:outline-none"
        />
        <div className="max-h-96 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500">No matches.</div>
          ) : (
            grouped.map((g) => (
              <div key={g.group}>
                <div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {g.group}
                </div>
                <ul>
                  {g.entries.map((e) => {
                    const idx = runningIdx++;
                    const active = idx === activeIdx;
                    return (
                      <li key={e.id}>
                        <button
                          type="button"
                          onClick={() => jump(e)}
                          onMouseEnter={() => setActiveIdx(idx)}
                          className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${
                            active ? "bg-primary text-white" : "hover:bg-gray-50 text-gray-800"
                          }`}
                        >
                          <span>{e.label}</span>
                          {e.hint && (
                            <span className={`text-xs ${active ? "text-white/70" : "text-gray-400"}`}>
                              {e.hint}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
        <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-400 flex items-center justify-between">
          <span>↑↓ navigate · ↵ open · esc close</span>
          <span>⌘K</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function scoreMatch(entry: PaletteEntry, needle: string): number {
  const haystack = [entry.label, entry.group, ...(entry.keywords ?? [])].join(" ").toLowerCase();
  if (haystack.includes(needle)) return 10;
  // Cheap subsequence match — useful for "vd" → "Venue Dashboard".
  let i = 0;
  let j = 0;
  while (i < needle.length && j < haystack.length) {
    if (needle[i] === haystack[j]) i++;
    j++;
  }
  return i === needle.length ? 1 : 0;
}
